import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '../ui/dialog';
import { LoadingSpinner } from '../icons/LoadingSpinner';
import { fetchUserAssets } from '../../services/backendService';

/**
 * Modal for selecting images from User Assets (from User Backend)
 */
export const UserAssetsPicker = ({ 
    isOpen, 
    onClose, 
    onSelect, 
    assetType = 'actors',  // 'actors', 'costumes', 'logos', 'products'
    title = 'Select from User Assets'
}) => {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeCategory, setActiveCategory] = useState(assetType);
    
    const categories = [
        { id: 'actors', label: 'Actors', icon: '👤' },
        { id: 'costumes', label: 'Costumes', icon: '👗' },
        { id: 'logos', label: 'Logos', icon: '🏷️' },
        { id: 'products', label: 'Products', icon: '📦' }
    ];

    useEffect(() => {
        if (isOpen) {
            loadAssets(activeCategory);
        }
    }, [isOpen, activeCategory]);

    const loadAssets = async (category) => {
        setLoading(true);
        setError('');
        
        try {
            const result = await fetchUserAssets(category);
            if (result.success) {
                setAssets(result.assets);
            } else {
                setError(result.error || 'Failed to load assets');
                setAssets([]);
            }
        } catch (e) {
            setError(e.message);
            setAssets([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (asset) => {
        // Get the image URL from the asset
        const imageUrl = asset.image_url || asset.imageUrl || asset.url || asset.image;
        if (imageUrl) {
            onSelect(imageUrl, asset);
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl w-[90vw] max-h-[85vh] bg-[#1a1a24] border-gray-700 p-0 overflow-hidden" data-testid="user-assets-picker">
                <DialogTitle className="sr-only">{title}</DialogTitle>
                
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-[#141418]">
                    <h2 className="text-lg font-semibold text-white">{title}</h2>
                </div>

                {/* Category Tabs */}
                <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-800 bg-[#141418]/50">
                    {categories.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                activeCategory === cat.id
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                            }`}
                            data-testid={`category-${cat.id}`}
                        >
                            <span>{cat.icon}</span>
                            {cat.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 140px)' }}>
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <LoadingSpinner className="w-8 h-8 animate-spin mb-3" />
                            <span>Loading {activeCategory}...</span>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center py-12 text-red-400">
                            <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                            <span>{error}</span>
                            <button 
                                onClick={() => loadAssets(activeCategory)}
                                className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"
                            >
                                Retry
                            </button>
                        </div>
                    ) : assets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                            <svg className="w-16 h-16 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-lg font-medium">No {activeCategory} found</span>
                            <span className="text-sm text-gray-600 mt-1">Upload assets in User Dashboard first</span>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {assets.map((asset, index) => {
                                const imageUrl = asset.image_url || asset.imageUrl || asset.url || asset.image;
                                const name = asset.name || asset.title || `Asset ${index + 1}`;
                                
                                return (
                                    <button
                                        key={asset.id || index}
                                        onClick={() => handleSelect(asset)}
                                        className="group relative bg-gray-800 rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-all"
                                        data-testid={`asset-${index}`}
                                    >
                                        <div className="aspect-square bg-gray-900">
                                            {imageUrl ? (
                                                <img 
                                                    src={imageUrl} 
                                                    alt={name}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div 
                                                className="w-full h-full items-center justify-center text-gray-600 hidden"
                                                style={{ display: imageUrl ? 'none' : 'flex' }}
                                            >
                                                <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className="p-2 bg-gray-800/90">
                                            <p className="text-sm font-medium text-white truncate">{name}</p>
                                        </div>
                                        {/* Hover overlay */}
                                        <div className="absolute inset-0 bg-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <span className="px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-full">
                                                Select
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-700 bg-[#141418]">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
